// src/services/feedApi.js — asking the server WHICH posts, and still reading
// them from Firestore, live.
//
// 🟥 THE SERVER RETURNS IDS, NOT POSTS, AND THAT IS THE WHOLE DESIGN. Ranking
//    needs the corpus and this person's interest vectors, which are both on the
//    server. Everything else about a post — its likes, its comments, an edit
//    thirty seconds old — is already live here through Firestore listeners and
//    the existing rules. Returning post bodies would mean a second, staler copy
//    of every post, a second set of rules to keep in step, and the loss of
//    realtime updates on the feed.
//
// 🟥 SO THE RANKED FEED IS STILL A LIVE FEED. The ids are subscribed to in
//    chunks of ten (Firestore's `in` limit) and merged back into the order the
//    ranker gave — the order IS the product, and an `in` query returns whatever
//    order it likes.
//
// 🟥 AND EVERY FAILURE FALLS BACK TO THE OLD FEED. `ranked: false` (the console
//    kill switch), a network error, an empty page — all three return null, and
//    the caller does exactly what it did before this existed.

import {
  collection, documentId, getDocs, limit, onSnapshot, orderBy, query, startAfter, where,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { postFromDoc } from './postCounts';
import { archivePick, ARCHIVE_PAGE, ARCHIVE_MAX_READS } from './feedPaging';

const BACKEND = process.env.REACT_APP_BACKEND_URL
  || 'https://signaling-server-prod-397203671414.us-central1.run.app';

/** Ask for a ranked page. Returns null when the caller should use its own feed. */
export async function rankedPage(surface, opts = {}) {
  try {
    const u = auth.currentUser;
    if (!u) return null;
    const idToken = await u.getIdToken();
    const res = await fetch(`${BACKEND}/api/feed/page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({
        surface,
        size: opts.size || (surface === 'reels' ? 12 : 20),
        exclude: (opts.exclude || []).slice(-200),
        /* 🟥 THE REFRESH FLAG WAS BUILT AND THEN DROPPED HERE. `pageRequest`
           returns `{ exclude, refresh }` and this forwarded only the first
           half, so a pull-to-refresh from the browser reached the server as an
           ordinary page — filtered by the seen ring, which is the whole thing a
           refresh is asking the server to relax. Silent, because the reply is a
           perfectly valid page; it is just the wrong one. */
        ...(opts.refresh ? { refresh: true } : {}),
      }),
    });
    if (!res.ok) return null;
    const body = await res.json();
    if (!body?.ok || body.ranked === false) return null;
    const posts = Array.isArray(body.posts) ? body.posts : [];
    return posts.length ? posts : null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to exactly these posts, in exactly this order.
 *
 * 🟢 ONE LISTENER PER TEN IDS. A page of twenty is two listeners, which is
 *    fewer than the screen already runs, and the whole page re-renders from a
 *    single merged map so a like landing on post nineteen does not reorder
 *    anything.
 *
 * 🟥 A POST THE RANKER CHOSE AND FIRESTORE REFUSED IS DROPPED, not left as a
 *    gap: it was deleted between the ranking and the read, or the rules say no.
 *    Neither is an error and neither is a hole in the page.
 */
export function watchRanked(ids, cb) {
  const wanted = (ids || []).filter(Boolean);
  if (!wanted.length) { cb([]); return () => {}; }

  const chunks = [];
  for (let i = 0; i < wanted.length; i += 10) chunks.push(wanted.slice(i, i + 10));

  const found = new Map();
  const emit = () => cb(wanted.map((id) => found.get(id)).filter(Boolean));

  const unsubs = chunks.map((chunk) => onSnapshot(
    query(collection(db, 'posts'), where(documentId(), 'in', chunk)),
    (snap) => {
      snap.forEach((d) => found.set(d.id, postFromDoc(d.id, d.data())));
      /* a document that disappears mid-session leaves the page rather than
         freezing as a copy of itself */
      snap.docChanges().forEach((c) => { if (c.type === 'removed') found.delete(c.doc.id); });
      emit();
    },
    () => emit(),
  ));

  return () => unsubs.forEach((u) => { try { u(); } catch { /* already gone */ } });
}

/**
 * 📚 One step of the archive walk — the next older posts nobody has been shown.
 *
 * 🟥 WHEN THE RANKER HAS NOTHING MORE, THE FEED STILL HAS MORE. An exhausted
 *    ranker (or one that is switched off) used to be the end of the feed and of
 *    the reels pane, with the platform full of posts nobody had been offered.
 *    See "WHEN THE RANKER HAS NOTHING MORE" in services/feedPaging.
 *
 * 🟢 ORDERED BY `timestamp` ALONE (no composite index) and continued from the
 *    last DOCUMENT read, so posts written in the same millisecond are never
 *    skipped or repeated. Reads a few pages at most looking for something new;
 *    `endedRef` is set once the collection itself runs out.
 */
export async function readArchive(cursorRef, endedRef, opts = {}) {
  const got = [];
  const seen = [...(opts.have || [])];
  const want = opts.want || 8;
  for (let i = 0; i < ARCHIVE_MAX_READS && got.length < want && !endedRef.current; i += 1) {
    const q = cursorRef.current
      ? query(collection(db, 'posts'), orderBy('timestamp', 'desc'), startAfter(cursorRef.current), limit(ARCHIVE_PAGE))
      : query(collection(db, 'posts'), orderBy('timestamp', 'desc'), limit(ARCHIVE_PAGE));
    const snap = await getDocs(q);
    if (snap.docs.length) cursorRef.current = snap.docs[snap.docs.length - 1];
    const { fresh, ended } = archivePick({
      incoming: snap.docs.map((d) => postFromDoc(d.id, d.data())),
      have: seen,
      pageSize: ARCHIVE_PAGE,
      blocked: opts.blocked,
      hidden: opts.hidden,
      friends: opts.friends,
      me: opts.me,
      videoOnly: !!opts.videoOnly,
    });
    if (ended) endedRef.current = true;
    for (const p of fresh) { seen.push(p.id); got.push(p); }
  }
  return got;
}

/** "Why am I seeing this?" — the same breakdown the ranker used. */
export async function whyThisPost(postId) {
  try {
    const u = auth.currentUser;
    if (!u || !postId) return null;
    const idToken = await u.getIdToken();
    const res = await fetch(`${BACKEND}/api/feed/why`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ postId }),
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body?.ok ? body : null;
  } catch {
    return null;
  }
}

/** "Start my feed again" — the person's own reset. */
export async function resetMyFeed() {
  try {
    const u = auth.currentUser;
    if (!u) return false;
    const idToken = await u.getIdToken();
    const res = await fetch(`${BACKEND}/api/feed/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({}),
    });
    return res.ok;
  } catch {
    return false;
  }
}
